import { trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { FromDictionaryPipe, LanguageService } from '@teamfoster/sdk/dictionary-ngrx';
import { StringUtility } from '@teamfoster/sdk/text-utility';
import { filter, firstValueFrom, Observable, Subject, takeUntil, takeWhile, tap } from 'rxjs';
import { CardGridAnimation } from 'src/app/animations';
import { PageData } from 'src/app/core/models';
import { getGeoLocationError, getGeoLocationLoaded, getGeoLocationPosition, RequestGeoLocation } from 'src/app/geo-location/store';
import { GeolocationPositionErrorLabels } from 'src/app/geo-location/util/error';
import { SetCanonical, SetTags, SetTitle } from 'src/app/store';
import { Initiative } from '../../models';
import {
  getInitiativesLoading,
  getInitiativesPage,
  getOrderedInitiatives,
  LoadInitiatives,
  LoadMoreInitiatives,
  SetSortOrder,
} from '../../store';
import { Theme } from 'src/app/themes/models';
import { getOrderedThemes } from 'src/app/themes/store';

@Component({
  selector: 'app-initiatives-overview',
  templateUrl: './initiatives-overview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('CardGridAnimation', [CardGridAnimation])],
})
export class InitiativesOverviewComponent implements OnInit, OnDestroy {
  initiatives$!: Observable<Initiative[]>;
  initiativesLoading$!: Observable<boolean>;
  geoLocation$!: Observable<GeolocationPosition | undefined>;
  geoLocationError$!: Observable<{ code: number; message: string } | undefined>;
  page$!: Observable<PageData>;
  themes$!: Observable<Theme[]>;
  filtersLoading = false;

  @Input({ alias: 'thema' }) theme: string = '';

  private dict = new FromDictionaryPipe(this.lang);

  sortForm = this.fb.group({
    orderBy: [0],
  });

  private _unsubscribe = new Subject<void>();

  sortOptions = [
    {
      value: 0,
      placeholder: 'initiatief-overzicht-sorteer-datum',
    },
    {
      value: 1,
      placeholder: 'initiatief-overzicht-sorteer-afstand',
    },
  ];

  sortLoaded = true;
  geoErrorLabel = GeolocationPositionErrorLabels;

  constructor(private store: Store, private lang: LanguageService, private fb: FormBuilder, private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.initiatives$ = this.store.select(getOrderedInitiatives);
    this.initiativesLoading$ = this.store.select(getInitiativesLoading);
    this.page$ = this.store.select(getInitiativesPage);
    this.geoLocation$ = this.store.select(getGeoLocationPosition);
    this.geoLocationError$ = this.store.select(getGeoLocationError);
    this.themes$ = this.store.select(getOrderedThemes);

    this.store.dispatch(
      SetTitle({ title: `${this.dict.transform('initiatief-overzicht-titel')} - ${this.dict.transform('meta-titel-suffix')}` })
    );

    this.store.dispatch(SetCanonical({ url: 'https://onzeijssel.nl/initiatieven' }));

    this.store.dispatch(
      SetTags({
        properties: {
          'description': StringUtility.Truncate(
            StringUtility.StripHTML(this.dict.transform('initiatief-overzicht-description')),
            157,
            '...'
          ),
          'og:title': `${this.dict.transform('initiatief-overzicht-titel')} ${this.dict.transform('meta-titel-suffix')}`,
          'og:description': StringUtility.Truncate(
            StringUtility.StripHTML(this.dict.transform('initiatief-overzicht-description')),
            157,
            '...'
          ),
        },
      })
    );

    this.sortForm.valueChanges.pipe(takeUntil(this._unsubscribe)).subscribe(a => this.setSortOrder(a));
  }

  trackByFn(index: number, item: Initiative) {
    return item.id;
  }

  // vote(direction: number, event: MouseEvent) {
  //   event.stopPropagation();
  //   event.preventDefault();
  // }
  loadMore() {
    this.store.dispatch(LoadMoreInitiatives());
  }

  async setSortOrder(e: any) {
    this.sortLoaded = false;

    if (e.orderBy === 1) {
      this.store.dispatch(RequestGeoLocation({}));

      this.sortLoaded = await firstValueFrom(this.store.select(getGeoLocationLoaded).pipe(filter(a => a)));
    }

    this.store.dispatch(SetSortOrder({ order: +e.orderBy }));
    this.store.dispatch(LoadInitiatives({ skip: 0, take: 12 }));

    this.sortLoaded = true;
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
